import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { Field } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';
import Illustration from '../components/Illustration.jsx';
import AuthShell, { AuthHead } from '../components/AuthShell.jsx';

/**
 * Step 1 of the reset flow: ask for the account's email.
 *
 * The confirmation screen is deliberately vague about whether the address is
 * registered — the API answers identically either way, and a UI that said
 * "no such account" would hand that back. Better to confirm the request and let
 * a genuine owner check their inbox.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/api/auth/forgot', { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthShell back={{ to: '/login', label: 'Back to sign in' }}>
        <div className="center">
          <Illustration name="success" width={180} />
          <h1 className="t-h1 mt-2">Check your inbox</h1>
          <p className="muted mt-1">
            If <strong>{email.trim()}</strong> has a HealAtHome account, we've sent a link to reset the
            password. It works once and expires in an hour.
          </p>

          <div className="banner banner-neutral mt-3" style={{ textAlign: 'left' }}>
            <Icon name="info" />
            <span className="grow tiny">
              Nothing arrived? Check the spam folder, or make sure you typed the address you registered with.
            </span>
          </div>

          <div className="stack-sm mt-3">
            <Link to="/login" className="btn btn-primary btn-block btn-lg">
              Back to sign in
            </Link>
            <button
              type="button"
              className="btn btn-ghost btn-block"
              onClick={() => {
                setSent(false);
                setError('');
              }}
            >
              Use a different email
            </button>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell back={{ to: '/login', label: 'Back to sign in' }}>
      <AuthHead
        title="Forgot your password?"
        subtitle="Enter the email you registered with and we'll send you a link to set a new one."
      />

      {error && (
        <div className="banner banner-danger">
          <Icon name="warning" />
          <span className="grow">{error}</span>
        </div>
      )}

      <form onSubmit={submit}>
        <Field label="Email address" id="fp-email">
          <input
            id="fp-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            required
          />
        </Field>

        <button className="btn btn-primary btn-lg btn-block" disabled={busy || !email.trim()}>
          {busy ? <span className="spinner" /> : 'Send reset link'}
        </button>
      </form>

      <p className="auth-foot">
        Remembered it? <Link to="/login">Sign in</Link> · New here?{' '}
        <Link to="/register">Create an account</Link>
      </p>
    </AuthShell>
  );
}
